/**
 * Sentry init with PII scrub.
 *
 * Mirrors apps/signage-tv/lib/main.dart's beforeSend / beforeBreadcrumb
 * scrubbers — drop HTTP request/response bodies and any signage.content
 * breadcrumb payloads before the event leaves the process.
 *
 * Release marker: signage-web-player@<APP_VERSION> so store-rollout
 * cohorts line up with the Flutter app's signage-tv@<APP_VERSION>.
 */

import * as Sentry from '@sentry/browser';

export interface SentryInitOptions {
  readonly dsn?: string;
  readonly appVersion: string;
  readonly environment?: 'production' | 'staging' | 'development';
  readonly tracesSampleRate?: number;
}

export function initSentry(opts: SentryInitOptions): void {
  const dsn = opts.dsn?.trim();
  if (!dsn) return; // No DSN configured — Sentry becomes a no-op.

  Sentry.init({
    dsn,
    release: `signage-web-player@${opts.appVersion}`,
    environment: opts.environment ?? guessEnv(),
    tracesSampleRate: opts.tracesSampleRate ?? 0.1,
    // Drop session replay entirely — signage payloads often include donor
    // names and we don't want any chance of them reaching Sentry.
    integrations: (defaults) => defaults.filter((i) => i.name !== 'Replay'),
    beforeSend: (event) => scrubEvent(event),
    beforeBreadcrumb: (breadcrumb) => scrubBreadcrumb(breadcrumb),
  });
}

/**
 * Strip potentially-sensitive payloads from events before send.
 * Kept exported for unit testing.
 */
export function scrubEvent<T extends Sentry.ErrorEvent>(event: T): T {
  if (event.request) {
    // Never ship request/response bodies — they contain donor names + amounts.
    event.request = { ...event.request, data: undefined } as typeof event.request;
  }
  if (event.extra && typeof event.extra === 'object') {
    const extra = { ...event.extra } as Record<string, unknown>;
    delete extra['body'];
    delete extra['response'];
    delete extra['signage.content'];
    event.extra = extra;
  }
  return event;
}

/** Drop any breadcrumb that would leak content-payload data. */
export function scrubBreadcrumb(bc: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
  if (bc.category === 'http' && bc.data) {
    // Keep the URL + status; drop the body.
    const { url, status_code, method } = bc.data as {
      url?: string;
      status_code?: number;
      method?: string;
    };
    return { ...bc, data: { url, status_code, method } };
  }
  if (bc.category?.startsWith('signage.content')) {
    return null;
  }
  return bc;
}

function guessEnv(): 'production' | 'staging' | 'development' {
  if (typeof window === 'undefined') return 'development';
  const host = window.location.host;
  if (host.includes('staging')) return 'staging';
  if (host.includes('localhost') || host.startsWith('127.')) return 'development';
  return 'production';
}
